import React from 'react';

import { DocumentType } from '@lglp/prismic-client';
import { useTranslation } from 'react-i18next';
import { Redirect, useParams } from 'react-router';

import { syncDocumentByUid } from '../../helpers/loading';
import useDocumentByUid from '../../hooks/useDocumentByUid';
import { SSRComponent } from '../../types/component';

const defaultRedirect = ({ from, to }: { from: string; to: string }) => {
  const CustomRedirect: SSRComponent = () => {
    return <Redirect to={to} />;
  };

  CustomRedirect.getInitialProps = () => {
    throw { status: 301, context: { url: to } };
  };

  return {
    path: from,
    component: CustomRedirect,
  };
};

const getPopinType = (popinUid: string) => {
  if (popinUid.startsWith('timeline-')) {
    return DocumentType.TIMELINE_POPIN;
  }
  if (popinUid.startsWith('slider-')) {
    return DocumentType.SLIDER_POPIN;
  }
  return DocumentType.MEDIA_POPIN;
};
const rsePopinRedirect = ({ from }: { from: string }) => {
  const CustomRedirect: SSRComponent = () => {
    const { popinUid } = useParams<{ popinUid: string }>();
    const documentType = getPopinType(popinUid);

    const { i18n } = useTranslation();
    const { error, data, loading } = useDocumentByUid(documentType, popinUid);

    if (data) {
      return (
        <Redirect
          to={`/${i18n.languages[0]}/focus/${
            i18n.languages[0] === 'fr'
              ? 'nos-actions-pour-lenvironnement'
              : 'what-we-do-for-the-environment'
          }/${popinUid}`}
        />
      );
    }

    if (loading || (!error && !data)) {
      return null;
    }
    return <Redirect to={`/${i18n.languages[0]}`} />;
  };

  CustomRedirect.getInitialProps = async (serverData) => {
    const popinUid = serverData.match.params.popinUid;
    const documentType = getPopinType(popinUid);
    const { data } = await syncDocumentByUid(
      documentType,
      popinUid,
      serverData
    );

    if (data) {
      throw {
        status: 301,
        context: {
          url: `/${serverData.language}/focus/${
            serverData.language === 'fr'
              ? 'nos-actions-pour-lenvironnement'
              : 'what-we-do-for-the-environment'
          }/${popinUid}`,
        },
      };
    }
    throw {
      status: 301,
      context: { url: `/${serverData.language}` },
    };
  };

  return {
    path: from,
    component: CustomRedirect,
  };
};

const articleRedirect = ({ from }: { from: string }) => {
  const CustomRedirect: SSRComponent = () => {
    const { articleUid } = useParams<{ articleUid: string }>();
    const { i18n } = useTranslation();

    const { error, data, loading } = useDocumentByUid(
      DocumentType.HOT_ARTICLE,
      articleUid
    );

    if (data) {
      return (
        <Redirect
          to={`/${i18n.languages[0]}/${
            i18n.languages[0] === 'fr' ? 'actualite' : 'news'
          }/${articleUid}`}
        />
      );
    }

    if (loading || (!error && !data)) {
      return null;
    }

    return <Redirect to={`/${i18n.languages[0]}/${articleUid}`} />;
  };

  CustomRedirect.getInitialProps = async (serverData) => {
    const articleUid = serverData.match.params.articleUid;
    const { data } = await syncDocumentByUid(
      DocumentType.HOT_ARTICLE,
      articleUid,
      serverData
    );

    if (data) {
      throw {
        status: 301,
        context: {
          url: `/${serverData.language}/${
            serverData.language === 'fr' ? 'actualite' : 'news'
          }/${articleUid}`,
        },
      };
    }

    throw {
      status: 301,
      context: { url: `/${serverData.language}/${articleUid}` },
    };
  };

  return {
    path: from,
    component: CustomRedirect,
  };
};

/**
 * Retourne la liste des redirections des pages investisseurs
 */
const INVESTORS_REDIRECT = [
  defaultRedirect({
    from: '/fr/chiffres-cles',
    to: '/fr/investisseurs/chiffres-cles',
  }),
  defaultRedirect({
    from: '/fr/communiques-de-presse(/.*)?',
    to: '/fr/investisseurs/communiques-de-presse',
  }),
  defaultRedirect({
    from: '/fr/publications(/.*)?',
    to: '/fr/investisseurs/information-reglementee',
  }),
  defaultRedirect({
    from: '/fr/(dette|notation|notations-finance)',
    to: '/fr/investisseurs/dette-et-notation-financiere',
  }),
  defaultRedirect({
    from: '/fr/notations-rse',
    to: '/fr/investisseurs/developpement-durable',
  }),
  defaultRedirect({
    from: '/fr/agenda-financier',
    to: '/fr/investisseurs/calendrier-financier',
  }),
  defaultRedirect({
    from: '/en/financial-key-figures',
    to: '/en/investors/key-figures',
  }),
  defaultRedirect({
    from: '/en/financial-press-release(/.*)?',
    to: '/en/investors/press-release',
  }),
  defaultRedirect({
    from: '/en/financial-publication(/.*)?',
    to: '/en/investors/regulated-information',
  }),
  defaultRedirect({
    from: '/en/(financial-debt|financial-ratings|finance-ratings)',
    to: '/en/investors/debt-and-financial-rating',
  }),
  defaultRedirect({
    from: '/en/csr-ratings',
    to: '/en/investors/sustainability',
  }),
  defaultRedirect({
    from: '/en/financial-calendar',
    to: '/en/investors/financial-calendar',
  }),
];

export const REDIRECT_ROUTES = [
  defaultRedirect({
    from: '/fr/activites',
    to: '/fr/nos-branches-d-activites',
  }),
  rsePopinRedirect({
    from: '/fr/nos-actions-pour-lenvironnement/:popinUid',
  }),
  rsePopinRedirect({
    from: '/en/what-we-do-for-the-environment/:popinUid',
  }),
  defaultRedirect({
    from: '/fr/nos-actions-pour-lenvironnement',
    to: '/fr/focus/nos-actions-pour-lenvironnement',
  }),
  defaultRedirect({
    from: '/fr/what-we-do-for-the-environment',
    to: '/fr/focus/what-we-do-for-the-environment',
  }),
  defaultRedirect({
    from: '/fr/contact',
    to: '/fr/contacts',
  }),
  defaultRedirect({
    from: '/en/contact',
    to: '/en/contacts',
  }),
  defaultRedirect({
    from: '/fr/innovation-par-la-poste',
    to: '/fr/l-innovation-au-coeur-du-groupe-la-poste',
  }),
  defaultRedirect({
    from: '/fr/geopost-le-colis-meme-a-lautre-bout-du-monde',
    to: '/fr/geopost-le-colis-meme-a-l-autre-bout-du-monde',
  }),
  defaultRedirect({
    from: '/fr/organigramme',
    to: '/fr/notre-gouvernance-le-comite-executif',
  }),
  defaultRedirect({
    from: '/en/services-mail-parcels',
    to: '/en/our-public-service-missions',
  }),
  defaultRedirect({
    from: '/fr/missions-de-service-public',
    to: '/fr/les-4-missions-de-service-public',
  }),
  defaultRedirect({
    from: '/en/la-banque-postale-the-bank-for-all',
    to: '/en/la-banque-postale',
  }),
  defaultRedirect({
    from: '/en/geopost--parcels-even-at-the-other-end-of-the-world',
    to: '/en/geopost-dpdgroup-parcel-delivery-in-france-and-abroad',
  }),
  defaultRedirect({
    from: '/fr/espace-presse/:pressReleaseDoc',
    to: '/fr/hub-communiques-de-presse',
  }),
  defaultRedirect({
    from: '/en/newsroom/:pressReleaseDoc',
    to: '/en/press-releases',
  }),
  articleRedirect({
    from: '/fr/article/:articleUid',
  }),
  articleRedirect({
    from: '/en/article/:articleUid',
  }),
  ...INVESTORS_REDIRECT,
];
